import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, Facebook, MessageCircle, Share2, X } from "lucide-react";
import { CONTACT } from "@/lib/site";

const HEADER_SOCIALS = [
  { label: "Instagram", href: CONTACT.instagramHref, icon: Instagram, testid: "header-social-instagram" },
  { label: "WhatsApp", href: CONTACT.whatsappHref, icon: MessageCircle, testid: "header-social-whatsapp" },
  { label: "Facebook", href: CONTACT.facebookHref, icon: Facebook, testid: "header-social-facebook" },
];

const NAV = [
  { label: "Tours & Packages", href: "#packages" },
  { label: "Ticketing & Visas", href: "#services" },
  { label: "Why Parth", href: "#why-parth" },
  { label: "Destinations", href: "#gallery" },
  { label: "Traveller Stories", href: "#reviews" },
  { label: "Contact Desk", href: "#contact" },
];

export const Header = () => (
  <header className="sticky top-0 z-40 bg-stone-50/90 backdrop-blur-md border-b border-stone-200/80" data-testid="site-header">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
      <a href="#top" className="flex items-center space-x-3" data-testid="brand-logo">
        <div className="w-10 h-10 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center font-bold text-lg tracking-wider">PT</div>
        <div>
          <span className="text-xl font-bold tracking-tight text-stone-900 block leading-tight">Parth Travels</span>
          <span className="text-xs text-stone-700 tracking-wide block uppercase font-semibold">Tours · Tickets · Visas</span>
        </div>
      </a>

      <nav className="hidden md:flex items-center space-x-8 text-sm font-medium text-stone-700" aria-label="Main Navigation">
        {NAV.map((item) => (
          <a key={item.href} href={item.href} data-testid={`nav-${item.href.slice(1)}`} className="hover:text-amber-800 transition-colors">
            {item.label}
          </a>
        ))}
      </nav>

      <div className="flex items-center space-x-2 sm:space-x-3">
        <div className="flex items-center space-x-1.5 pr-1 sm:pr-2 border-r border-stone-200">
          {HEADER_SOCIALS.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target={s.href.startsWith("http") ? "_blank" : undefined}
              rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
              data-testid={s.testid}
              aria-label={s.label}
              title={s.label}
              className="w-8 h-8 rounded-full bg-stone-100 hover:bg-amber-100 border border-stone-200 text-stone-700 hover:text-amber-800 flex items-center justify-center transition-colors"
            >
              <s.icon className="w-4 h-4" aria-hidden="true" />
            </a>
          ))}
        </div>
        <a
          href="#contact"
          data-testid="header-plan-trip-btn"
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-amber-700 hover:bg-amber-800 rounded-lg shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-700 focus:ring-offset-2"
        >
          Plan Your Trip
        </a>
      </div>
    </div>
  </header>
);

const FLOATING_SOCIALS = [
  { label: "Instagram", href: CONTACT.instagramHref, testid: "floating-instagram-btn", cls: "bg-gradient-to-tr from-amber-500 via-rose-500 to-fuchsia-600 hover:from-amber-600 hover:via-rose-600 hover:to-fuchsia-700", icon: Instagram },
  { label: "Facebook", href: CONTACT.facebookHref, testid: "floating-facebook-btn", cls: "bg-sky-700 hover:bg-sky-600", icon: Facebook },
  { label: "WhatsApp", href: CONTACT.whatsappHref, testid: "floating-whatsapp-btn", cls: "bg-emerald-600 hover:bg-emerald-700", icon: null },
];

export const FloatingSocials = () => {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-3" data-testid="floating-socials">
      <AnimatePresence>
        {open &&
          FLOATING_SOCIALS.map((s, i) => (
            <motion.a
              key={s.label}
              href={s.href}
              target={s.href.startsWith("http") ? "_blank" : undefined}
              rel={s.href.startsWith("http") ? "noopener noreferrer" : undefined}
              data-testid={s.testid}
              aria-label={s.label}
              title={s.label}
              initial={{ opacity: 0, y: 12, scale: 0.6 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.6 }}
              transition={{ duration: 0.2, delay: i * 0.05 }}
              className={`w-14 h-14 rounded-full ${s.cls} text-white flex items-center justify-center shadow-xl transition-transform hover:-translate-y-1`}
            >
              {s.icon ? (
                <s.icon className="w-7 h-7" aria-hidden="true" />
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
                  <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm0 18.03c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.26 8.26 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 4.54 0 8.24 3.7 8.24 8.24 0 4.55-3.7 8.24-8.24 8.24zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.25-.64.8-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.14-1.18-.06-.1-.22-.16-.47-.28z" />
                </svg>
              )}
            </motion.a>
          ))}
      </AnimatePresence>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="floating-socials-toggle"
        aria-label={open ? "Hide social buttons" : "Show social buttons"}
        aria-expanded={open}
        className="w-14 h-14 rounded-full bg-amber-700 hover:bg-amber-800 text-white flex items-center justify-center shadow-xl transition-all hover:-translate-y-0.5"
      >
        {open ? <X className="w-6 h-6" aria-hidden="true" /> : <Share2 className="w-6 h-6" aria-hidden="true" />}
      </button>
    </div>
  );
};
